import { createTheme } from "@mui/material/styles";
import type { TypographyVariantName } from "./tokens";
import { tokens } from "./tokens";

/**
 * The single `createTheme` call (ADR-20260821-8msf, superseded twice but
 * this rule survives both supersessions — see its standards note in
 * TG-2). Material UI's internals are not restyled and no library
 * component is re-implemented here: every value below is read from
 * `tokens.ts` and handed to `createTheme` in Material UI's own shape.
 *
 * AC-4: this is the only place the theme is constructed. A component-level
 * focus-visible outline is set once here via `MuiCssBaseline` overrides
 * rather than per-component, for the same reason.
 */

// ADR-20261102-h2fd: the FRP theme's own variant rule -- the mobile size by default and the full
// size from the `sm` breakpoint, so headings step down on a phone exactly as H2's do.
function muiVariant(variant: (typeof tokens)["typography"]["variants"][TypographyVariantName]) {
  return {
    fontSize: variant.mobileSize,
    fontWeight: variant.weight,
    lineHeight: variant.lineHeight,
    letterSpacing: variant.letterSpacing,
    [`@media (min-width:${tokens.layout.breakpoints.sm}px)`]: { fontSize: variant.size },
  };
}

const c = tokens.colour;
const radius = tokens.interactionState.radius;
const target = tokens.interactionState.targetSize;
const border = tokens.interactionState.border;
const bp = tokens.layout.breakpoints;

const focusRing = {
  outline: `${tokens.interactionState.focusRing.width}px solid ${tokens.interactionState.focusRing.colourOnLight}`,
  outlineOffset: `${tokens.interactionState.focusRing.offset}px`,
};

const selectionControl = {
  color: c.neutral.borderControl,
  padding: tokens.spacing.sm,
  "&.Mui-checked": { color: c.brand[700] },
};

function alertTone(tone: { readonly text: string; readonly surface: string }) {
  return { backgroundColor: tone.surface, borderLeftColor: tone.text, color: c.neutral.ink };
}

export const theme = createTheme({
  spacing: 4, // theme.spacing(1) === tokens.spacing.xxs (4px); see tokens.ts
  shape: {
    borderRadius: tokens.interactionState.radius.control,
  },
  palette: {
    primary: {
      main: tokens.colour.brand[700],
      dark: tokens.colour.brand[900],
      light: tokens.colour.brand[300],
      contrastText: tokens.colour.neutral.white,
    },
    secondary: {
      // accent.500 is fill-only; white on it is forbidden (DESIGN-TOKENS.md),
      // so its contrast text is the navy brand colour, never white.
      main: tokens.colour.accent[500],
      contrastText: tokens.colour.brand[900],
    },
    error: {
      main: tokens.colour.status.error.text,
      light: tokens.colour.status.error.surface,
    },
    success: {
      main: tokens.colour.status.success.text,
      light: tokens.colour.status.success.surface,
    },
    warning: {
      main: tokens.colour.status.warning.text,
      light: tokens.colour.status.warning.surface,
    },
    info: {
      main: tokens.colour.status.info.text,
      light: tokens.colour.status.info.surface,
    },
    background: {
      default: tokens.colour.neutral.surface,
      paper: tokens.colour.neutral.white,
    },
    text: {
      primary: tokens.colour.neutral.ink,
      secondary: tokens.colour.neutral.inkMuted,
      disabled: tokens.colour.neutral.inkDisabled,
    },
    divider: tokens.colour.neutral.borderSubtle,
  },
  breakpoints: {
    values: tokens.layout.breakpoints,
  },
  typography: {
    fontFamily: tokens.typography.fontFamily,
    // Only 400 and 700 exist (DESIGN-TOKENS.md); Material UI's "medium" maps to bold, as the FRP
    // theme maps it.
    fontWeightRegular: tokens.typography.weights.regular,
    fontWeightMedium: tokens.typography.weights.bold,
    fontWeightBold: tokens.typography.weights.bold,
    h1: muiVariant(tokens.typography.variants.display),
    h2: muiVariant(tokens.typography.variants.heading1),
    h3: muiVariant(tokens.typography.variants.heading2),
    h4: muiVariant(tokens.typography.variants.heading3),
    // ADR-20261016-v6nd (AC-2's "eyebrow casing"): the design system's own
    // Typography spec ("Uppercase is confined to eyebrow") renders this
    // variant uppercase -- the earlier `textTransform: "none"` override
    // here flattened every eyebrow (H2's "Renewal summary", "Step 1 of 3",
    // "Your sections", etc.) to sentence case, the opposite of H2's actual
    // screenshots. `button` below keeps its own "none" override; that one
    // is correct (MUI's default Button uppercasing, not this rule).
    overline: { ...muiVariant(tokens.typography.variants.eyebrow), textTransform: "uppercase" },
    subtitle1: muiVariant(tokens.typography.variants.bodyLarge),
    body1: muiVariant(tokens.typography.variants.body),
    body2: muiVariant(tokens.typography.variants.bodySmall),
    caption: muiVariant(tokens.typography.variants.helper),
    button: { ...muiVariant(tokens.typography.variants.action), textTransform: "none" },
  },
  transitions: {
    duration: {
      shortest: tokens.interactionState.motion.duration.fast,
      shorter: tokens.interactionState.motion.duration.fast,
      short: tokens.interactionState.motion.duration.base,
      standard: tokens.interactionState.motion.duration.base,
      complex: tokens.interactionState.motion.duration.slow,
      enteringScreen: tokens.interactionState.motion.duration.base,
      leavingScreen: tokens.interactionState.motion.duration.fast,
    },
    easing: {
      easeInOut: tokens.interactionState.motion.easing,
      easeOut: tokens.interactionState.motion.easing,
      easeIn: tokens.interactionState.motion.easing,
      sharp: tokens.interactionState.motion.easing,
    },
  },
  // ADR-20261102-h2fd (m1-portal-h2-fidelity AC-7): the 12 component overrides of the FRP theme
  // in H2's design-system bundle (`window.FRPPortalDS.theme.components`), value for value, from
  // tokens. `__tests__/frp-theme.test.ts` fails on any value that drifts from the bundle's
  // snapshot. MuiButtonBase is ours alone; the guard does not compare components the FRP theme
  // leaves untouched.
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": { colorScheme: "light" },
        body: {
          backgroundColor: c.neutral.white,
          color: c.neutral.ink,
          minWidth: tokens.layout.minViewport,
        },
        "*:focus-visible": focusRing,
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "0.01ms !important",
            transitionDuration: "0.01ms !important",
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, variant: "contained" },
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          minHeight: target.touch,
          padding: `${tokens.spacing.sm}px ${tokens.spacing.lg}px`,
          [`@media (min-width:${bp.md}px)`]: { minHeight: target.standard },
          "&.Mui-focusVisible": focusRing,
        },
        contained: {
          backgroundColor: c.brand[700],
          color: c.neutral.white,
          "&:hover": { backgroundColor: c.brand[900] },
          "&.Mui-disabled": { backgroundColor: c.neutral.borderDisabled, color: c.neutral.white },
        },
        outlined: {
          borderWidth: border.emphasisWidth,
          borderColor: c.brand[700],
          color: c.brand[700],
          "&:hover": { borderWidth: border.emphasisWidth, backgroundColor: c.brand[50] },
        },
        text: {
          color: c.brand[700],
          textDecoration: "underline",
          "&:hover": { backgroundColor: c.brand[50] },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: c.neutral.white,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: c.neutral.borderControl },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: c.brand[500] },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: c.brand[700],
            borderWidth: border.emphasisWidth,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: c.status.error.text,
            borderWidth: border.emphasisWidth,
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: c.neutral.borderDisabled },
        },
        input: {
          minHeight: target.standard - tokens.spacing.lg,
          padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
        },
      },
    },
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          position: "static",
          transform: "none",
          color: c.neutral.ink,
          fontWeight: tokens.typography.weights.bold,
          marginBottom: tokens.spacing.xxs,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginTop: tokens.spacing.xxs,
          color: c.neutral.inkMuted,
          "&.Mui-error": { color: c.status.error.text },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radius.surface,
          borderLeftWidth: tokens.spacing.xxs,
          borderLeftStyle: "solid",
          padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
        },
        standardError: alertTone(c.status.error),
        standardWarning: alertTone(c.status.warning),
        standardInfo: alertTone(c.status.info),
        standardSuccess: alertTone(c.status.success),
      },
    },
    MuiCheckbox: { styleOverrides: { root: selectionControl } },
    MuiRadio: { styleOverrides: { root: selectionControl } },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": { color: c.brand[700] },
          "&.Mui-checked + .MuiSwitch-track": { backgroundColor: c.brand[500], opacity: 1 },
        },
        track: { backgroundColor: c.neutral.borderControl, opacity: 1 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { borderRadius: radius.surface, backgroundImage: "none" } },
    },
    MuiLink: {
      defaultProps: { underline: "always" },
      styleOverrides: {
        root: {
          color: c.brand[700],
          textUnderlineOffset: "0.2em",
          "&:focus-visible": focusRing,
        },
      },
    },
    MuiContainer: {
      defaultProps: { maxWidth: false },
      styleOverrides: {
        root: {
          maxWidth: tokens.layout.containerMax,
          paddingInline: tokens.layout.gutters.xs,
          [`@media (min-width:${bp.sm}px)`]: { paddingInline: tokens.layout.gutters.sm },
          [`@media (min-width:${bp.md}px)`]: { paddingInline: tokens.layout.gutters.md },
        },
      },
    },
  },
});

// The FRP theme's elevation steps (H2 bundle: shadows[1..3] = raised, card, overlay).
theme.shadows[1] = tokens.interactionState.elevation.raised;
theme.shadows[2] = tokens.interactionState.elevation.card;
theme.shadows[3] = tokens.interactionState.elevation.overlay;
